import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader } from "./adminShared";
import { Mail, Plus, Loader2, Pencil, Send, Trash2, X, ShieldCheck, AlertTriangle, Globe } from "lucide-react";

const ST = {
  to_research: { label: "To Research", cls: "bg-slate-100 text-slate-600" },
  ready_for_review: { label: "Ready for Review", cls: "bg-indigo-100 text-indigo-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  sent: { label: "Sent", cls: "bg-blue-100 text-blue-700" },
  replied: { label: "Replied", cls: "bg-teal-100 text-teal-700" },
  bounced: { label: "Bounced", cls: "bg-red-100 text-red-700" },
  follow_up: { label: "Follow-up", cls: "bg-amber-100 text-amber-700" },
};
const STATUS_KEYS = Object.keys(ST);
const FILTERS = [{ key: "", label: "All" }, { key: "unsent", label: "Unsent" }, ...STATUS_KEYS.map((k) => ({ key: k, label: ST[k].label }))];
const EMPTY = { organizer_name: "", race_name: "", race_date: "", email: "", website: "", notes: "", status: "to_research" };
const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#22d3ee] focus:border-[#22d3ee] outline-none";

function Badge({ status }) {
  const s = ST[status] || ST.to_research;
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${s.cls}`} data-testid={`contact-status-${status}`}>{s.label}</span>;
}

export default function AdminOutreach() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ counts: {}, total: 0, unsent: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);    // contact object or EMPTY for new
  const [compose, setCompose] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/outreach/contacts`, { params: { status: filter, q } }),
      api.get(`/admin/outreach/stats`),
    ]).then(([c, s]) => { setRows(c.data.contacts || []); setStats(s.data); })
      .catch(() => toast.error("Failed to load outreach contacts"))
      .finally(() => setLoading(false));
  }, [filter, q]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (c, status) => {
    setBusy(c.id);
    try { await api.put(`/admin/outreach/contacts/${c.id}`, { status }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete outreach contact "${c.organizer_name} — ${c.race_name}"? This only removes it from your outreach list.`)) return;
    setBusy(c.id);
    try { await api.delete(`/admin/outreach/contacts/${c.id}`); toast.success("Contact removed"); load(); }
    catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-outreach-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <AdminHeader title="Organizer Outreach" subtitle="Manage prospect organizers and send personalised invitations — one at a time, with your explicit confirmation." />
        <button onClick={() => setEdit(EMPTY)} data-testid="outreach-add-btn"
          className="inline-flex items-center gap-2 bg-[#0a1420] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-[#1e293b] shrink-0">
          <Plus size={15} /> Add Contact
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="outreach-filters">
        {FILTERS.map((f) => {
          const n = f.key === "" ? stats.total : f.key === "unsent" ? stats.unsent : (stats.counts?.[f.key] ?? 0);
          const active = filter === f.key;
          return (
            <button key={f.key || "all"} onClick={() => setFilter(f.key)} data-testid={`outreach-filter-${f.key || "all"}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-[#22d3ee] border-[#22d3ee] text-[#0a1420]" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
              {f.label} <span className="opacity-60">({n})</span>
            </button>
          );
        })}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organizer, race or email…"
          data-testid="outreach-search" className="ml-auto border border-slate-200 rounded-full px-4 py-1.5 text-sm w-full sm:w-64 outline-none focus:border-[#22d3ee]" />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        {loading ? <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" /></div> :
          rows.length === 0 ? <p className="text-slate-500 text-sm py-10 text-center" data-testid="outreach-empty">No contacts here yet. Click “Add Contact” to start building your outreach list.</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="text-slate-400 text-xs uppercase"><tr>
                <th className="text-left pb-2">Organizer</th><th className="text-left pb-2">Race</th><th className="text-left pb-2">Date</th>
                <th className="text-left pb-2">Email</th><th className="text-left pb-2">Status</th><th className="text-right pb-2">Actions</th>
              </tr></thead>
              <tbody>{rows.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 align-top" data-testid={`outreach-row-${c.id}`}>
                  <td className="py-3 font-semibold text-slate-800">{c.organizer_name}</td>
                  <td className="py-3 text-slate-600">{c.race_name}</td>
                  <td className="py-3 text-slate-500 whitespace-nowrap">{c.race_date ? String(c.race_date).slice(0, 10) : "—"}</td>
                  <td className="py-3 text-slate-600 break-all">{c.email}</td>
                  <td className="py-3">
                    <select value={c.status} onChange={(e) => setStatus(c, e.target.value)} disabled={busy === c.id} data-testid={`outreach-status-select-${c.id}`}
                      className={`text-[11px] font-bold rounded px-2 py-1 border-0 cursor-pointer ${(ST[c.status] || ST.to_research).cls}`}>
                      {STATUS_KEYS.map((k) => <option key={k} value={k}>{ST[k].label}</option>)}
                    </select>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => setCompose(c)} data-testid={`outreach-compose-${c.id}`}
                        className="inline-flex items-center gap-1 bg-[#22d3ee] text-[#0a1420] text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-[#06b6d4]"><Send size={13} /> Email</button>
                      <button onClick={() => setEdit(c)} data-testid={`outreach-edit-${c.id}`}
                        className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-slate-50"><Pencil size={13} /></button>
                      <button onClick={() => del(c)} disabled={busy === c.id} data-testid={`outreach-delete-${c.id}`}
                        className="inline-flex items-center gap-1 border border-red-200 text-red-600 text-xs font-bold px-2.5 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-40"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
      </div>

      {edit && <ContactModal initial={edit} onClose={() => setEdit(null)} onDone={load} />}
      {compose && <ComposeModal contact={compose} onClose={() => setCompose(null)} onDone={load} />}
    </div>
  );
}

function Field({ label, children }) {
  return <label className="block"><span className="text-xs font-bold text-slate-500 uppercase tracking-wide">{label}</span><div className="mt-1">{children}</div></label>;
}

function ContactModal({ initial, onClose, onDone }) {
  const isNew = !initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.organizer_name.trim() || !f.race_name.trim() || !f.email.trim()) return toast.error("Organizer, race and email are required");
    setSaving(true);
    try {
      const body = { organizer_name: f.organizer_name, race_name: f.race_name, race_date: f.race_date, email: f.email, website: f.website, notes: f.notes, status: f.status };
      if (isNew) await api.post(`/admin/outreach/contacts`, body);
      else await api.put(`/admin/outreach/contacts/${initial.id}`, body);
      toast.success(isNew ? "Contact added" : "Contact updated");
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Overlay onClose={onClose} testid="outreach-contact-modal" title={isNew ? "Add Outreach Contact" : "Edit Contact"}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Organizer Name"><input value={f.organizer_name} onChange={(e) => set("organizer_name", e.target.value)} className={inputCls} data-testid="outreach-f-organizer" /></Field>
        <Field label="Race Name"><input value={f.race_name} onChange={(e) => set("race_name", e.target.value)} className={inputCls} data-testid="outreach-f-race" /></Field>
        <Field label="Race Date"><input type="date" value={f.race_date ? String(f.race_date).slice(0, 10) : ""} onChange={(e) => set("race_date", e.target.value)} className={inputCls} data-testid="outreach-f-date" /></Field>
        <Field label="Email"><input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="outreach-f-email" /></Field>
        <Field label="Website"><input value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className={inputCls} data-testid="outreach-f-website" /></Field>
        <Field label="Status"><select value={f.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="outreach-f-status">{STATUS_KEYS.map((k) => <option key={k} value={k}>{ST[k].label}</option>)}</select></Field>
      </div>
      <Field label="Notes"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className={inputCls} data-testid="outreach-f-notes" /></Field>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-bold">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="outreach-save-contact" className="px-4 py-2 rounded-lg bg-[#0a1420] text-white text-sm font-bold disabled:opacity-40 inline-flex items-center gap-2">{saving && <Loader2 size={14} className="animate-spin" />} Save</button>
      </div>
    </Overlay>
  );
}

function ComposeModal({ contact, onClose, onDone }) {
  const [subject, setSubject] = useState(contact.draft_subject || "");
  const [body, setBody] = useState(contact.draft_body || "");
  const [sending, setSending] = useState(false);
  const [allowResend, setAllowResend] = useState(false);
  const from = "YourRaceFinder <jp@yourracefinder.com>";
  const alreadySent = !!contact.last_sent_at || ["sent", "replied", "follow_up"].includes(contact.status);
  const approved = contact.status === "approved";
  const canSend = subject.trim() && body.trim() && !sending && (alreadySent ? allowResend : approved);

  const send = async () => {
    if (!canSend) return;
    if (!window.confirm(`Send ONE email now?\n\nTo: ${contact.email}\nSubject: ${subject}\n\nThis sends a real email via Resend.`)) return;
    setSending(true);
    try {
      await api.post(`/admin/outreach/send`, { contact_id: contact.id, subject, body, confirm: true, allow_resend: allowResend });
      toast.success(`Email sent to ${contact.email}`);
      onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed — nothing was marked as delivered"); }
    finally { setSending(false); }
  };

  return (
    <Overlay onClose={onClose} testid="outreach-compose-modal" title={`Email ${contact.organizer_name}`} wide>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Composer */}
        <div className="space-y-4">
          <Field label="Subject"><input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="compose-subject" placeholder="A quick invitation to list your race on YourRaceFinder" /></Field>
          <Field label="Message"><textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className={inputCls} data-testid="compose-body" placeholder={`Hi ${contact.organizer_name},\n\n…`} /></Field>
          <p className="text-xs text-slate-400">Plain text only — links and formatting are sent safely. Replies go to your jp@yourracefinder.com inbox.</p>
        </div>
        {/* Preview */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="compose-preview">
          <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Preview</div>
          <div className="text-xs text-slate-500 space-y-0.5 mb-3">
            <div><span className="font-bold text-slate-600">From:</span> {from}</div>
            <div data-testid="preview-to"><span className="font-bold text-slate-600">To:</span> {contact.organizer_name} &lt;{contact.email}&gt;</div>
            <div data-testid="preview-subject"><span className="font-bold text-slate-600">Subject:</span> {subject || <span className="italic text-slate-400">—</span>}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-800 whitespace-pre-wrap min-h-[180px]" data-testid="preview-body">{body || <span className="italic text-slate-400">Your message preview appears here…</span>}</div>
        </div>
      </div>

      {/* Send gate */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        {!approved && !alreadySent && (
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="compose-approve-gate">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" /> Set this contact's status to <strong>Approved</strong> before you can send.
          </div>
        )}
        {alreadySent && (
          <label className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3 cursor-pointer" data-testid="compose-resend-warning">
            <input type="checkbox" checked={allowResend} onChange={(e) => setAllowResend(e.target.checked)} data-testid="compose-allow-resend" className="mt-0.5" />
            <span><AlertTriangle size={14} className="inline mr-1" /> This organizer was already contacted for this race. Tick to allow sending again.</span>
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 text-sm font-bold">Cancel</button>
          <button onClick={send} disabled={!canSend} data-testid="compose-send-btn"
            className="px-5 py-2 rounded-lg bg-[#22d3ee] text-[#0a1420] text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Confirm &amp; Send
          </button>
        </div>
      </div>
    </Overlay>
  );
}

function Overlay({ title, children, onClose, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-2xl"} my-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-display font-bold text-lg text-slate-800 flex items-center gap-2"><Mail size={18} className="text-[#06b6d4]" /> {title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="outreach-modal-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}
